import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import InvitationDetector from './components/auth/InvitationDetector';
import RequireAuth from './components/guards/RequireAuth';
import RequireRole from './components/guards/RequireRole';
import MainLayout from './components/layout/MainLayout';
import LoadingScreen from './components/common/LoadingScreen';

// Superadmin Pages
const OrganizationCreation = lazy(() => import('./pages/admin/OrganizationCreation'));
const OrganizationList = lazy(() => import('./pages/admin/OrganizationList'));
const OrganizationEdit = lazy(() => import('./pages/admin/OrganizationEdit'));
const OrganizationAdminUsers = lazy(() => import('./pages/admin/OrganizationAdminUsers'));
const OrganizationDetailPage = lazy(() => import('./pages/admin/OrganizationDetails'));
const GlobalConfiguration = lazy(() => import('./pages/admin/GlobalConfiguration'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProfilePage = lazy(() => import('./pages/userProfile/ProfilePage'));

// Auth Pages
const InvitationPage = lazy(() => import('./pages/Auth/InvitationPage'));
const AuthentikCallback = lazy(() => import('./components/AuthentikCallback'));
const Logout = lazy(() => import('./pages/Logout'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'auth0';

const authRoutes: RouteObject[] = AUTH_PROVIDER === 'authentik'
  ? [{ path: 'callback', element: <Suspense fallback={<LoadingScreen />}><AuthentikCallback /></Suspense> }]
  : [{ path: 'invite', element: <Suspense fallback={<LoadingScreen />}><InvitationPage /></Suspense> }];

// Define routes - only superadmin accessible
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <InvitationDetector />
        <RequireAuth>
          <MainLayout />
        </RequireAuth>
      </Suspense>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" />,
      },
      {
        path: 'dashboard',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <RequireRole redirectTo="/unauthorized">
              <Dashboard />
            </RequireRole>
          </Suspense>
        ),
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <RequireRole redirectTo="/unauthorized">
              <ProfilePage />
            </RequireRole>
          </Suspense>
        ),
      },
      // SuperAdmin specific routes
      {
        path: 'admin',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <Dashboard />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'organization',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <OrganizationList />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'create-organization',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <OrganizationCreation />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'organization-admin-users',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <OrganizationAdminUsers />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'organization/:id',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <OrganizationDetailPage />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'organization/:id/edit',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <OrganizationEdit />
                </RequireRole>
              </Suspense>
            ),
          },
          {
            path: 'global-config',
            element: (
              <Suspense fallback={<LoadingScreen />}>
                <RequireRole redirectTo="/unauthorized">
                  <GlobalConfiguration />
                </RequireRole>
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  // Auth routes — invite is Auth0 only; callback is Authentik only
  ...authRoutes,
  // Dedicated logout URL — outside RequireAuth so it works regardless of auth state
  {
    path: 'logout',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Logout />
      </Suspense>
    ),
  },
  // Catch-all route for 404
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <NotFound />
      </Suspense>
    ),
  },
  // Unauthorized access route
  {
    path: 'unauthorized',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Unauthorized />
      </Suspense>
    ),
  },
];

export default routes;